import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatDateOnly } from '../../utils/dateUtils';
import { getCurrencySymbol } from '../../utils/currencySymbol';


/**
 * Generic History Dialog Component
 * Currently optimized for warranty claims history (POST request with nested response structure)
 * Can be extended for other entity types in the future
 * 
 * @param {Object} props
 * @param {Function} props.hideDialog - Function to hide the dialog
 * @param {boolean} props.dialogVisible - Whether dialog is visible
 * @param {Object} props.entity - The entity object (e.g., warrantyClaim)
 * @param {string} props.entityType - Type of entity ('warrantyClaim', etc.)
 * @param {Function} props.useHistoryQuery - Hook function to fetch history data (required)
 * @param {Array} props.columns - Column configuration for the history table
 * @param {string} props.titleKey - Translation key for dialog title
 * @param {string} props.entityIdentifierField - Field name to use as entity identifier in title
 */
const GenericHistoryDialog = ({ 
    hideDialog, 
    dialogVisible, 
    entity, 
    entityType,
    useHistoryQuery,
    columns,
    titleKey,
    entityIdentifierField = 'id'
}) => {
    const { th } = useLanguage();
    
    // State for history data
    const [historyData, setHistoryData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [error, setError] = useState(null);
    
    // State for visible columns - use all columns by default
    const [visibleColumns, setVisibleColumns] = useState(columns?.map(col => col.field) || []);
    
    // Reference for DataTable export
    const dt = useRef(null);

    // Skip query if no entity or not visible
    const shouldFetch = dialogVisible && entity && entity.id;

    // Always call the hook - it's required
    const {
        data: historyRecords,
        isLoading: queryLoading,
        isError: queryError,
        error: queryErrorData
    } = useHistoryQuery({
        id: entity?.id || 0,
        pageNumber: 0,
        pageSize: 100,
        languageId: 2
    }, {
        skip: !shouldFetch,
        refetchOnMountOrArgChange: true
    });

    // Update local state based on query results
    useEffect(() => {
        setIsLoading(queryLoading);
        setIsError(queryError);
        setError(queryErrorData);
        
        if (historyRecords) {
            console.log('History Records received:', historyRecords);
            
            if (Array.isArray(historyRecords)) {
                setHistoryData(historyRecords);
            } else if (historyRecords && Array.isArray(historyRecords.data)) {
                setHistoryData(historyRecords.data);
            } else {
                setHistoryData([]);
            }
        } else {
            setHistoryData([]);
        }
    }, [historyRecords, queryLoading, queryError, queryErrorData]);

    // Column selection component
    const columnSelectionTemplate = () => {
        if (!columns || columns.length === 0) return null;
        
        return (
            <MultiSelect
                value={visibleColumns}
                options={columns.map((col) => ({ label: col.header, value: col.field }))}
                onChange={(e) => setVisibleColumns(e.value)}
                optionLabel="label"
                placeholder={th('common.selectColumns')}
                className="w-full"
                display="chip"
                maxSelectedLabels={3}
                selectedItemsLabel={th('common.selectedItems')}
                panelStyle={{ width: '300px' }}
            />
        );
    };

    // Export to CSV
    const exportCSV = () => {
        if (dt.current) {
            dt.current.exportCSV();
        }
    };

    // Header template with column selection and export buttons
    const headerTemplate = () => {
        return (
            <div className="flex flex-column sm:flex-row w-full justify-content-end align-items-stretch sm:align-items-center gap-2">
                <div className="w-full sm:w-auto">{columnSelectionTemplate()}</div>
                <Button 
                    type="button" 
                    icon="pi pi-file" 
                    label={th('common.exportCSV')} 
                    onClick={exportCSV} 
                    className="p-button-outlined w-full sm:w-auto" 
                />
            </div>
        );
    };

    // Dialog footer with close button
    const renderDialogFooter = () => {
        return (
            <div className="flex justify-content-end gap-2">
                <Button 
                    label={th('common.close')} 
                    icon="pi pi-times" 
                    outlined 
                    onClick={hideDialog} 
                    type="button" 
                />
            </div>
        );
    };

    // Get entity identifier for title
    const getEntityIdentifier = () => {
        if (!entity) return '';
        
        // Try different possible identifier fields
        const possibleFields = [entityIdentifierField, 'preAppNo', 'warrClaimsNo', 'id'];
        for (const field of possibleFields) {
            if (entity[field]) {
                return entity[field];
            }
        }
        return entity.id || '';
    };

    // Get dialog title
    const getDialogTitle = () => {
        const identifier = getEntityIdentifier();
        const baseTitle = th(titleKey || 'common.history');
        return identifier ? `${baseTitle} - ${identifier}` : baseTitle;
    };

    // Get export filename
    const getExportFilename = () => {
        const identifier = getEntityIdentifier();
        return `${th('common.history')}_${identifier || 'detay'}`;
    };

    return (
        <Dialog 
            visible={dialogVisible} 
            style={{ width: '90%', maxWidth: '1200px' }} 
            header={getDialogTitle()} 
            modal 
            className="p-fluid" 
            footer={renderDialogFooter()} 
            onHide={hideDialog} 
            maximizable
        >
            <Tooltip target=".export-buttons>button" position="bottom" />

            {isLoading && (
                <div className="flex justify-content-center">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                </div>
            )}

            {isError && (
                <div className="p-error">
                    {th('common.loadingError').replace('{0}', error?.message || th('common.unknownError'))}
                </div>
            )}

            {!isLoading && !isError && (!historyData || historyData.length === 0) && (
                <div className="text-center p-4">{th('common.noHistoryFound')}</div>
            )}

            {!isLoading && !isError && historyData && historyData.length > 0 && columns && (
                <DataTable
                    ref={dt}
                    value={historyData}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    className="p-datatable-gridlines"
                    showGridlines
                    emptyMessage={th('common.noHistoryFound')}
                    sortField="transactionDate"
                    sortOrder={-1}
                    header={headerTemplate}
                    resizableColumns
                    columnResizeMode="expand"
                    scrollable
                    scrollHeight="flex"
                    size="small"
                    responsiveLayout="scroll"
                    exportFilename={getExportFilename()}
                >
                    {columns
                        .filter((col) => visibleColumns.includes(col.field))
                        .map((col) => (
                            <Column 
                                key={col.field} 
                                field={col.field} 
                                header={col.header} 
                                body={col.body} 
                                sortable={col.sortable} 
                                frozen={col.frozen} 
                                alignFrozen={col.alignFrozen} 
                            />
                        ))}
                </DataTable>
            )}
        </Dialog>
    );
};

export default GenericHistoryDialog;
